import { getAlmInfo } from "@/backend/read/BackendReader";
import type { FieldChangeName } from "@/model/change";
import type { Icon } from "@/model/icon";

import BoardChange from "./internal/change/BoardChange.vue";
import IterationChange from "./internal/change/IterationChange.vue";
import LinkChange from "./internal/change/LinkChange.vue";
import PointsChange from "./internal/change/PointsChange.vue";
import PriorityChange from "./internal/change/PriorityChange.vue";
import ReactionsChange from "./internal/change/ReactionsChange.vue";
import RiskChange from "./internal/change/RiskChange.vue";
import StatusChange from "./internal/change/StatusChange.vue";
import StickerChange from "./internal/change/StickerChange.vue";
import TeamChange from "./internal/change/TeamChange.vue";
import TextChange from "./internal/change/TextChange.vue";
import TypeChange from "./internal/change/TypeChange.vue";
import UserChange from "./internal/change/UserChange.vue";

interface StickyChangesMeta {
  key: string;
  keyNew: string;
  keyField: string;
  component: unknown;
  icon: Icon;
}

export const stickyChanges: Record<FieldChangeName, StickyChangesMeta> = {
  // field changes
  text: {
    key: /*$t*/ "stickyChange.summary",
    keyNew: /*$t*/ "stickyChange.summary.new",
    keyField: /*$t*/ "stickyChange.summary.field",
    component: TextChange,
    icon: "general/summary",
  },
  type: {
    key: /*$t*/ "stickyChange.stickyType",
    keyNew: /*$t*/ "stickyChange.stickyType.new",
    keyField: /*$t*/ "stickyChange.stickyType.field",
    component: TypeChange,
    icon: "general/sticky",
  },
  points: {
    key: /*$t*/ "stickyChange.storyPoints",
    keyNew: /*$t*/ "stickyChange.storyPoints.new",
    keyField: /*$t*/ "stickyChange.storyPoints.field",
    component: PointsChange,
    icon: "general/points",
  },
  priority: {
    key: /*$t*/ "stickyChange.wsjf",
    keyNew: /*$t*/ "stickyChange.wsjf.new",
    keyField: /*$t*/ "stickyChange.wsjf.field",
    component: PriorityChange,
    icon: "general/points",
  },
  risk: {
    key: /*$t*/ "stickyChange.risk",
    keyNew: /*$t*/ "stickyChange.risk.new",
    keyField: /*$t*/ "stickyChange.risk.field",
    component: RiskChange,
    icon: "board/risk",
  },
  iterationId: {
    key: /*$t*/ "stickyChange.iteration",
    keyNew: /*$t*/ "stickyChange.iteration.new",
    keyField: /*$t*/ "stickyChange.iteration.field",
    component: IterationChange,
    icon: "general/iteration",
  },
  teamId: {
    key: /*$t*/ "stickyChange.team",
    keyNew: /*$t*/ "stickyChange.team.new",
    keyField: /*$t*/ "stickyChange.team.field",
    component: TeamChange,
    icon: "persona/team",
  },
  reactions: {
    key: /*$t*/ "stickyChange.reactions",
    keyNew: "",
    keyField: /*$t*/ "stickyChange.reactions.field",
    component: ReactionsChange,
    icon: "general/sticky-reactions",
  },
  flagType: {
    key: /*$t*/ "stickyChange.flag",
    keyNew: /*$t*/ "stickyChange.flag.new",
    keyField: /*$t*/ "stickyChange.flag.field",
    component: StickerChange,
    icon: "general/sticker",
  },
  dependTeamId: {
    key: /*$t*/ "stickyChange.enabling",
    keyNew: /*$t*/ "stickyChange.enabling.new",
    keyField: /*$t*/ "stickyChange.enabling.field",
    component: TeamChange,
    icon: "persona/enabling-team",
  },
  assignee: {
    key: /*$t*/ "stickyChange.assignee",
    keyNew: /*$t*/ "stickyChange.assignee.new",
    keyField: /*$t*/ "stickyChange.assignee.field",
    component: UserChange,
    icon: "persona/user",
  },
  reporter: {
    key: /*$t*/ "stickyChange.reporter",
    keyNew: /*$t*/ "stickyChange.reporter.new",
    keyField: /*$t*/ "stickyChange.reporter.field",
    component: UserChange,
    icon: "persona/user",
  },
  status: {
    key: /*$t*/ "stickyChange.status",
    keyNew: /*$t*/ "stickyChange.status.new",
    keyField: /*$t*/ "stickyChange.status.field",
    component: StatusChange,
    icon: "general/status",
  },
  almId: {
    key: /*$t*/ "stickyChange.almId",
    keyNew: /*$t*/ "stickyChange.almId.new",
    keyField: /*$t*/ "stickyChange.almId.field",
    component: TextChange,
    get icon() {
      return getAlmInfo().icon;
    },
  },
  // other changes
  mirrorBoard: {
    key: "",
    keyNew: "",
    keyField: /*$t*/ "stickyChange.mirror.field",
    component: BoardChange,
    icon: "general/mirror",
  },
  link: {
    key: "",
    keyNew: "",
    keyField: /*$t*/ "stickyChange.link.field",
    component: LinkChange,
    icon: "general/link",
  },
  create: {
    key: "",
    keyNew: "",
    keyField: /*$t*/ "stickyChange.created.field",
    component: null,
    icon: "general/add-new",
  },
  delete: {
    key: "",
    keyNew: "",
    keyField: /*$t*/ "stickyChange.deleted.field",
    component: null,
    icon: "general/delete",
  },
} as const;
